import { useState } from "react";
import { Crown, RefreshCw, Plus, Trash2, Zap, Timer, X } from "lucide-react";
import { api } from "@/lib/api";
import { useAzure } from "@/context/AzureContext";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";

export default function Pim() {
  const { state, run, refresh } = useAzure();
  const [openEl, setOpenEl] = useState(false);
  const [openAct, setOpenAct] = useState(null);
  const [elForm, setElForm] = useState({ principal_id: "", role: "", scope: "Directory (tenant)", max_duration_hours: 8 });
  const [actForm, setActForm] = useState({ justification: "", duration_hours: 1 });

  const eligible = state?.pim_eligible || [];
  const active = state?.pim_active || [];
  const users = state?.entra_users || [];
  const groups = state?.entra_groups || [];
  const roles = state?.identity_catalog?.directory_roles || [];

  const addEligible = () =>
    run(() => api.post("/azure/identity/pim/eligible", elForm), { success: "Eligible assignment created" })
      .then((r) => { if (r.ok) setOpenEl(false); });

  const removeEligible = (e) => { if (window.confirm(`Remove eligibility '${e.role}' for ${e.principal_name}?`)) run(() => api.delete(`/azure/identity/pim/eligible/${e.id}`), { success: "Eligibility removed" }); };

  const activate = () =>
    run(() => api.post("/azure/identity/pim/activate", { eligible_id: openAct.id, ...actForm }), { success: `Activated '${openAct.role}'` })
      .then((r) => { if (r.ok) { setOpenAct(null); setActForm({ justification: "", duration_hours: 1 }); } });

  const deactivate = (a) => run(() => api.post(`/azure/identity/pim/deactivate/${a.id}`), { success: "Role deactivated" });

  const isActive = (elId) => active.some((a) => a.eligible_id === elId);

  return (
    <div data-testid="az-pim">
      <div className="rounded-lg bg-gradient-to-r from-[#4b2e83] to-[#0078d4] text-white p-4 mb-4">
        <div className="flex items-center gap-2 font-semibold"><Crown className="h-4 w-4" /> Privileged Identity Management</div>
        <div className="text-white/80 text-xs mt-1">Grant just-in-time, time-bound access to privileged directory roles instead of permanent assignments.</div>
      </div>

      <Tabs defaultValue="eligible">
        <TabsList>
          <TabsTrigger value="eligible" data-testid="az-pim-tab-eligible">Eligible assignments</TabsTrigger>
          <TabsTrigger value="active" data-testid="az-pim-tab-active">Active assignments</TabsTrigger>
        </TabsList>

        <TabsContent value="eligible" className="mt-4">
          <div className="flex items-center gap-2 border-b border-border pb-3 mb-3">
            <Button size="sm" onClick={() => { setElForm({ principal_id: "", role: "", scope: "Directory (tenant)", max_duration_hours: 8 }); setOpenEl(true); }} data-testid="az-pim-new-btn" className="bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2"><Plus className="h-4 w-4" /> Add eligible assignment</Button>
            <Button size="sm" variant="ghost" onClick={refresh} className="gap-2"><RefreshCw className="h-4 w-4" /> Refresh</Button>
          </div>
          <div className="border border-border rounded-md overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Principal</TableHead><TableHead>Role</TableHead><TableHead>Scope</TableHead><TableHead>Max duration</TableHead><TableHead className="w-52">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {eligible.map((e) => (
                  <TableRow key={e.id} data-testid={`az-pim-el-${e.principal_name.replace(/\s/g, "-")}`}>
                    <TableCell className="font-medium">{e.principal_name} <span className="text-xs text-muted-foreground">({e.principal_type})</span></TableCell>
                    <TableCell><Badge variant="outline" className="text-xs">{e.role}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{e.scope}</TableCell>
                    <TableCell className="text-xs">{e.max_duration_hours}h</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button size="sm" variant="outline" className="h-7 gap-1.5 text-xs" data-testid={`az-pim-activate-${e.principal_name.replace(/\s/g, "-")}`} disabled={isActive(e.id)} onClick={() => setOpenAct(e)}><Zap className="h-3.5 w-3.5" /> {isActive(e.id) ? "Active" : "Activate"}</Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => removeEligible(e)}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {eligible.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-8">No eligible assignments.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="active" className="mt-4">
          <div className="border border-border rounded-md overflow-hidden">
            <Table>
              <TableHeader><TableRow><TableHead>Principal</TableHead><TableHead>Role</TableHead><TableHead>Justification</TableHead><TableHead>Duration</TableHead><TableHead>Activated</TableHead><TableHead className="w-32"></TableHead></TableRow></TableHeader>
              <TableBody>
                {active.map((a) => (
                  <TableRow key={a.id} data-testid={`az-pim-active-${a.principal_name.replace(/\s/g, "-")}`}>
                    <TableCell className="font-medium">{a.principal_name}</TableCell>
                    <TableCell><Badge className="text-xs bg-emerald-100 text-emerald-700">{a.role}</Badge></TableCell>
                    <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">{a.justification}</TableCell>
                    <TableCell className="text-xs"><span className="inline-flex items-center gap-1"><Timer className="h-3.5 w-3.5" /> {a.duration_hours}h</span></TableCell>
                    <TableCell className="text-xs text-muted-foreground">{new Date(a.activated_at).toLocaleString()}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" className="h-7 text-xs text-red-600" onClick={() => deactivate(a)}>Deactivate</Button></TableCell>
                  </TableRow>
                ))}
                {active.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-muted-foreground py-8">No active activations. Activate an eligible role to elevate.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add eligible */}
      <Dialog open={openEl} onOpenChange={setOpenEl}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Add eligible assignment</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Principal (user or group)</Label>
              <Select value={elForm.principal_id} onValueChange={(v) => setElForm({ ...elForm, principal_id: v })}>
                <SelectTrigger data-testid="az-pim-principal"><SelectValue placeholder="Select principal" /></SelectTrigger>
                <SelectContent>
                  {users.map((u) => <SelectItem key={u.object_id} value={u.object_id}>{u.display_name} (User)</SelectItem>)}
                  {groups.map((g) => <SelectItem key={g.object_id} value={g.object_id}>{g.name} (Group)</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Directory role</Label>
              <Select value={elForm.role} onValueChange={(v) => setElForm({ ...elForm, role: v })}>
                <SelectTrigger data-testid="az-pim-role"><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>{roles.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Max activation duration (hours)</Label>
              <Input type="number" min={1} max={24} value={elForm.max_duration_hours} onChange={(e) => setElForm({ ...elForm, max_duration_hours: Number(e.target.value) })} />
            </div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpenEl(false)}>Cancel</Button><Button data-testid="az-pim-create-submit" onClick={addEligible} disabled={!elForm.principal_id || !elForm.role} className="bg-[#0078d4] hover:bg-[#106ebe] text-white">Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Activate */}
      <Dialog open={!!openAct} onOpenChange={(v) => !v && setOpenAct(null)}>
        <DialogContent className="max-w-md" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Activate '{openAct?.role}'</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label>Justification *</Label><Textarea data-testid="az-pim-justification" value={actForm.justification} onChange={(e) => setActForm({ ...actForm, justification: e.target.value })} placeholder="Why do you need this role right now?" /></div>
            <div className="space-y-1.5"><Label>Duration (hours, max {openAct?.max_duration_hours})</Label><Input type="number" min={1} max={openAct?.max_duration_hours} value={actForm.duration_hours} onChange={(e) => setActForm({ ...actForm, duration_hours: Number(e.target.value) })} data-testid="az-pim-duration" /></div>
          </div>
          <DialogFooter><Button variant="ghost" onClick={() => setOpenAct(null)}>Cancel</Button><Button data-testid="az-pim-activate-submit" onClick={activate} disabled={!actForm.justification.trim()} className="bg-[#0078d4] hover:bg-[#106ebe] text-white gap-2"><Zap className="h-4 w-4" /> Activate</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
